import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api';
import JsBarcode from 'jsbarcode';
import { 
  Printer, 
  Settings, 
  Search, 
  Plus, 
  Minus, 
  Trash2, 
  Eye,
  Download,
  Package,
  Barcode,
  Tag,
  FileText,
  Loader2,
  Save,
  RefreshCw,
  Grid3X3
} from 'lucide-react';

// Modelos de folhas de etiquetas comuns no Brasil
const MODELOS_FOLHA = {
  'pimaco_6080': { nome: 'Pimaco 6080', colunas: 3, linhas: 10, largura: 25.4, altura: 66.7, margem_sup: 12.7, margem_esq: 4.7, esp_h: 0, esp_v: 0 },
  'pimaco_6081': { nome: 'Pimaco 6081', colunas: 3, linhas: 8, largura: 25.4, altura: 84.7, margem_sup: 13, margem_esq: 4.7, esp_h: 0, esp_v: 0 },
  'pimaco_6082': { nome: 'Pimaco 6082', colunas: 2, linhas: 5, largura: 101.6, altura: 50.8, margem_sup: 12.7, margem_esq: 3.2, esp_h: 0, esp_v: 0 },
  'pimaco_6083': { nome: 'Pimaco 6083', colunas: 2, linhas: 10, largura: 50.8, altura: 25.4, margem_sup: 12.7, margem_esq: 0, esp_h: 0, esp_v: 0 },
  'pimaco_6184': { nome: 'Pimaco 6184', colunas: 2, linhas: 7, largura: 84.7, altura: 33.9, margem_sup: 21.2, margem_esq: 13.3, esp_h: 5.3, esp_v: 0 },
  'pimaco_6287': { nome: 'Pimaco 6287', colunas: 3, linhas: 11, largura: 33.9, altura: 23.8, margem_sup: 10.7, margem_esq: 7.9, esp_h: 0, esp_v: 0 },
  'colacril_ca4354': { nome: 'Colacril CA4354', colunas: 4, linhas: 13, largura: 45, altura: 20, margem_sup: 10, margem_esq: 10, esp_h: 2.5, esp_v: 1 },
  'gondola_pequena': { nome: 'Gôndola Pequena', colunas: 4, linhas: 10, largura: 45, altura: 25, margem_sup: 10, margem_esq: 10, esp_h: 5, esp_v: 3 },
  'gondola_media': { nome: 'Gôndola Média', colunas: 3, linhas: 7, largura: 60, altura: 35, margem_sup: 10, margem_esq: 10, esp_h: 5, esp_v: 5 },
  'gondola_grande': { nome: 'Gôndola Grande', colunas: 2, linhas: 5, largura: 90, altura: 50, margem_sup: 10, margem_esq: 10, esp_h: 5, esp_v: 5 },
  'termica_40x25': { nome: 'Térmica 40x25mm', colunas: 1, linhas: 1, largura: 40, altura: 25, margem_sup: 0, margem_esq: 0, esp_h: 0, esp_v: 0 },
  'termica_50x30': { nome: 'Térmica 50x30mm', colunas: 1, linhas: 1, largura: 50, altura: 30, margem_sup: 0, margem_esq: 0, esp_h: 0, esp_v: 0 },
  'termica_60x40': { nome: 'Térmica 60x40mm', colunas: 1, linhas: 1, largura: 60, altura: 40, margem_sup: 0, margem_esq: 0, esp_h: 0, esp_v: 0 },
  'personalizado': { nome: 'Personalizado', colunas: 3, linhas: 10, largura: 30, altura: 25, margem_sup: 10, margem_esq: 10, esp_h: 2, esp_v: 2 },
};

const EtiquetasPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [imprimindo, setImprimindo] = useState(false);
  const [produtos, setProdutos] = useState([]);
  const [produtosSelecionados, setProdutosSelecionados] = useState([]);
  const [busca, setBusca] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [activeTab, setActiveTab] = useState('imprimir');
  const previewRef = useRef(null);
  
  // Configurações
  const [config, setConfig] = useState({
    modelo_folha: 'pimaco_6080',
    tipo_impressora: 'laser', // laser, jato_tinta, termica
    // Dimensões personalizadas (em mm)
    largura_etiqueta: 25.4,
    altura_etiqueta: 66.7,
    colunas: 3,
    linhas: 10,
    margem_superior: 12.7,
    margem_esquerda: 4.7,
    espacamento_horizontal: 0,
    espacamento_vertical: 0,
    // Conteúdo da etiqueta
    mostrar_nome: true,
    mostrar_codigo_barras: true,
    mostrar_preco: true,
    mostrar_codigo_interno: false,
    mostrar_descricao: false,
    // Formatação
    tamanho_fonte_nome: 10,
    tamanho_fonte_preco: 14,
    tamanho_codigo_barras: 40, // altura em pixels
    formato_codigo_barras: 'CODE128', // CODE128, EAN13, EAN8, UPC
    prefixo_preco: 'R$',
    // Impressora térmica
    impressora_termica_nome: '',
    largura_bobina: 40,
  });

  // Carregar configurações salvas
  useEffect(() => {
    const carregarConfig = async () => {
      try {
        const response = await api.get('/settings/etiquetas');
        if (response.data && Object.keys(response.data).length > 0) {
          setConfig(prev => ({ ...prev, ...response.data }));
        }
      } catch (error) {
        console.log('Usando configurações padrão');
      }
    };
    carregarConfig();
  }, []);

  // Buscar produtos
  const buscarProdutos = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/products', { 
        params: { search: busca, limit: 50 } 
      });
      setProdutos(response.data || []);
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os produtos",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [busca, toast]);

  useEffect(() => {
    buscarProdutos();
  }, [buscarProdutos]);

  // Atualizar config quando mudar modelo de folha
  useEffect(() => {
    if (config.modelo_folha !== 'personalizado') {
      const modelo = MODELOS_FOLHA[config.modelo_folha];
      if (modelo) {
        setConfig(prev => ({
          ...prev,
          largura_etiqueta: modelo.largura,
          altura_etiqueta: modelo.altura,
          colunas: modelo.colunas,
          linhas: modelo.linhas,
          margem_superior: modelo.margem_sup,
          margem_esquerda: modelo.margem_esq,
          espacamento_horizontal: modelo.esp_h,
          espacamento_vertical: modelo.esp_v,
        }));
      }
    }
  }, [config.modelo_folha]);

  // Salvar configurações
  const salvarConfig = async () => {
    setSalvando(true);
    try {
      await api.post('/settings/etiquetas', config);
      toast({
        title: "Sucesso",
        description: "Configurações salvas com sucesso"
      });
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar as configurações",
        variant: "destructive"
      });
    } finally {
      setSalvando(false);
    }
  };

  // Adicionar produto à lista
  const adicionarProduto = (produto) => {
    const existe = produtosSelecionados.find(p => p.id === produto.id);
    if (existe) {
      setProdutosSelecionados(prev => 
        prev.map(p => p.id === produto.id ? { ...p, quantidade: p.quantidade + 1 } : p)
      );
    } else {
      setProdutosSelecionados(prev => [...prev, { ...produto, quantidade: 1 }]);
    }
  };

  // Alterar quantidade
  const alterarQuantidade = (produtoId, delta) => {
    setProdutosSelecionados(prev => 
      prev.map(p => {
        if (p.id === produtoId) {
          const novaQtd = Math.max(1, p.quantidade + delta);
          return { ...p, quantidade: novaQtd };
        }
        return p;
      })
    );
  };

  // Remover produto
  const removerProduto = (produtoId) => {
    setProdutosSelecionados(prev => prev.filter(p => p.id !== produtoId));
  };

  // Formatar preço
  const formatarPreco = (valor) => {
    return new Intl.NumberFormat('pt-BR', { 
      style: 'currency', 
      currency: 'BRL' 
    }).format(valor || 0);
  };

  // Gerar código de barras SVG
  const gerarCodigoBarras = (codigo, largura, altura) => {
    if (!codigo) return null;
    
    try {
      const canvas = document.createElement('canvas');
      JsBarcode(canvas, codigo, {
        format: config.formato_codigo_barras,
        width: 2,
        height: altura,
        displayValue: true,
        fontSize: 10,
        margin: 2,
      });
      return canvas.toDataURL('image/png');
    } catch (error) {
      console.error('Erro ao gerar código de barras:', error);
      return null;
    }
  };

  // Gerar HTML para impressão
  const gerarHTMLImpressao = () => {
    const etiquetas = [];
    produtosSelecionados.forEach(produto => {
      for (let i = 0; i < produto.quantidade; i++) {
        etiquetas.push(produto);
      }
    });

    const larguraEtiqueta = config.largura_etiqueta;
    const alturaEtiqueta = config.altura_etiqueta;
    const colunas = config.colunas;
    const linhas = config.linhas;
    const margemSup = config.margem_superior;
    const margemEsq = config.margem_esquerda;
    const espH = config.espacamento_horizontal;
    const espV = config.espacamento_vertical;

    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Etiquetas de Produtos</title>
        <style>
          @page {
            size: A4;
            margin: 0;
          }
          body {
            margin: 0;
            padding: 0;
            font-family: Arial, sans-serif;
          }
          .pagina {
            width: 210mm;
            height: 297mm;
            padding-top: ${margemSup}mm;
            padding-left: ${margemEsq}mm;
            page-break-after: always;
            box-sizing: border-box;
          }
          .pagina:last-child {
            page-break-after: auto;
          }
          .grid-etiquetas {
            display: grid;
            grid-template-columns: repeat(${colunas}, ${larguraEtiqueta}mm);
            grid-template-rows: repeat(${linhas}, ${alturaEtiqueta}mm);
            gap: ${espV}mm ${espH}mm;
          }
          .etiqueta {
            width: ${larguraEtiqueta}mm;
            height: ${alturaEtiqueta}mm;
            box-sizing: border-box;
            padding: 2mm;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            text-align: center;
            overflow: hidden;
            border: 0.1mm dashed #ccc;
          }
          .nome-produto {
            font-size: ${config.tamanho_fonte_nome}pt;
            font-weight: bold;
            line-height: 1.1;
            max-height: ${config.tamanho_fonte_nome * 2.5}pt;
            overflow: hidden;
            word-break: break-word;
          }
          .preco {
            font-size: ${config.tamanho_fonte_preco}pt;
            font-weight: bold;
            color: #000;
            margin-top: 1mm;
          }
          .codigo-barras {
            margin: 1mm 0;
          }
          .codigo-barras img {
            max-width: 100%;
            height: ${config.tamanho_codigo_barras}px;
          }
          .codigo-interno {
            font-size: 8pt;
            color: #666;
          }
          .descricao {
            font-size: 7pt;
            color: #666;
            max-height: 12pt;
            overflow: hidden;
          }
          @media print {
            .etiqueta {
              border: none;
            }
          }
        </style>
      </head>
      <body>
    `;

    const etiquetasPorPagina = colunas * linhas;
    const totalPaginas = Math.ceil(etiquetas.length / etiquetasPorPagina);

    for (let pagina = 0; pagina < totalPaginas; pagina++) {
      html += '<div class="pagina"><div class="grid-etiquetas">';
      
      const inicio = pagina * etiquetasPorPagina;
      const fim = Math.min(inicio + etiquetasPorPagina, etiquetas.length);
      
      for (let i = inicio; i < fim; i++) {
        const produto = etiquetas[i];
        const codigoBarras = gerarCodigoBarras(
          produto.barcode || produto.code || produto.id?.substring(0, 12), 
          larguraEtiqueta * 3, 
          config.tamanho_codigo_barras
        );
        
        html += '<div class="etiqueta">';
        
        if (config.mostrar_nome) {
          html += `<div class="nome-produto">${produto.name || 'Produto'}</div>`;
        }
        
        if (config.mostrar_codigo_barras && codigoBarras) {
          html += `<div class="codigo-barras"><img src="${codigoBarras}" alt="Código de Barras" /></div>`;
        }
        
        if (config.mostrar_preco) {
          html += `<div class="preco">${formatarPreco(produto.price)}</div>`;
        }
        
        if (config.mostrar_codigo_interno && (produto.code || produto.barcode)) {
          html += `<div class="codigo-interno">Cód: ${produto.code || produto.barcode}</div>`;
        }
        
        if (config.mostrar_descricao && produto.description) {
          html += `<div class="descricao">${produto.description.substring(0, 50)}</div>`;
        }
        
        html += '</div>';
      }
      
      // Preencher células vazias
      for (let i = fim; i < inicio + etiquetasPorPagina; i++) {
        html += '<div class="etiqueta"></div>';
      }
      
      html += '</div></div>';
    }

    html += '</body></html>';
    return html;
  };

  // Imprimir
  const imprimir = () => {
    if (produtosSelecionados.length === 0) {
      toast({
        title: "Aviso",
        description: "Selecione pelo menos um produto",
        variant: "warning"
      });
      return;
    }

    setImprimindo(true);
    
    try {
      const html = gerarHTMLImpressao();
      const printWindow = window.open('', '_blank');
      printWindow.document.write(html);
      printWindow.document.close();
      
      setTimeout(() => {
        printWindow.print();
        setImprimindo(false);
      }, 500);
    } catch (error) {
      console.error('Erro ao imprimir:', error);
      toast({
        title: "Erro",
        description: "Não foi possível gerar a impressão",
        variant: "destructive"
      });
      setImprimindo(false);
    }
  };

  // Preview
  const abrirPreview = () => {
    if (produtosSelecionados.length === 0) {
      toast({
        title: "Aviso",
        description: "Selecione pelo menos um produto para visualizar",
        variant: "warning"
      });
      return;
    }
    setShowPreview(true);
  };

  // Total de etiquetas
  const totalEtiquetas = produtosSelecionados.reduce((acc, p) => acc + p.quantidade, 0);
  const etiquetasPorPagina = config.colunas * config.linhas;
  const totalPaginas = Math.ceil(totalEtiquetas / etiquetasPorPagina);

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Tag className="h-8 w-8" />
            Etiquetas de Produtos
          </h1>
          <p className="text-muted-foreground">Imprima etiquetas de gôndola com código de barras e preço</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="imprimir" className="gap-2">
            <Printer className="h-4 w-4" />
            Imprimir Etiquetas
          </TabsTrigger>
          <TabsTrigger value="configuracoes" className="gap-2">
            <Settings className="h-4 w-4" />
            Configurações
          </TabsTrigger>
        </TabsList>

        {/* Tab Imprimir */}
        <TabsContent value="imprimir" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Busca de Produtos */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Produtos Disponíveis
                </CardTitle>
                <CardDescription>Busque e selecione os produtos para imprimir etiquetas</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Buscar por nome ou código..."
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && buscarProdutos()}
                  />
                  <Button onClick={buscarProdutos} disabled={loading}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  </Button>
                </div>

                <div className="max-h-[400px] overflow-y-auto space-y-2">
                  {produtos.map((produto) => (
                    <div 
                      key={produto.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 cursor-pointer transition-colors"
                      onClick={() => adicionarProduto(produto)}
                    >
                      <div className="flex-1">
                        <div className="font-medium">{produto.name}</div>
                        <div className="text-sm text-muted-foreground flex gap-4">
                          <span>{formatarPreco(produto.price)}</span>
                          {produto.barcode && <span className="font-mono">{produto.barcode}</span>}
                        </div>
                      </div>
                      <Button size="sm" variant="ghost">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {produtos.length === 0 && !loading && (
                    <div className="text-center py-8 text-muted-foreground">
                      Nenhum produto encontrado
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Produtos Selecionados */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Barcode className="h-5 w-5" />
                  Etiquetas a Imprimir
                </CardTitle>
                <CardDescription>
                  {totalEtiquetas} etiqueta(s) em {totalPaginas} página(s)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {produtosSelecionados.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Tag className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Clique nos produtos à esquerda para adicionar</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {produtosSelecionados.map((produto) => (
                      <div 
                        key={produto.id}
                        className="flex items-center justify-between p-3 border rounded-lg"
                      >
                        <div className="flex-1">
                          <div className="font-medium">{produto.name}</div>
                          <div className="text-sm text-muted-foreground">{formatarPreco(produto.price)}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            size="icon" 
                            variant="outline"
                            onClick={() => alterarQuantidade(produto.id, -1)}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="w-12 text-center font-bold">{produto.quantidade}</span>
                          <Button 
                            size="icon" 
                            variant="outline"
                            onClick={() => alterarQuantidade(produto.id, 1)}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="icon" 
                            variant="ghost"
                            className="text-red-500"
                            onClick={() => removerProduto(produto.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <Separator />

                <div className="flex gap-2">
                  <Button 
                    className="flex-1 gap-2" 
                    variant="outline"
                    onClick={abrirPreview}
                    disabled={produtosSelecionados.length === 0}
                  >
                    <Eye className="h-4 w-4" />
                    Visualizar
                  </Button>
                  <Button 
                    className="flex-1 gap-2" 
                    onClick={imprimir}
                    disabled={produtosSelecionados.length === 0 || imprimindo}
                  >
                    {imprimindo ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Printer className="h-4 w-4" />
                    )}
                    Imprimir
                  </Button>
                </div>

                {/* Info do modelo */}
                <div className="text-sm text-muted-foreground bg-accent/50 p-3 rounded-lg">
                  <div className="font-medium mb-1">Modelo: {MODELOS_FOLHA[config.modelo_folha]?.nome}</div>
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    <span>{config.colunas} colunas x {config.linhas} linhas</span>
                    <span>{config.largura_etiqueta}mm x {config.altura_etiqueta}mm</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab Configurações */}
        <TabsContent value="configuracoes" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Modelo de Folha */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Grid3X3 className="h-5 w-5" />
                  Modelo de Folha
                </CardTitle>
                <CardDescription>Selecione o modelo de etiqueta ou personalize</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Modelo de Folha</Label>
                  <Select 
                    value={config.modelo_folha} 
                    onValueChange={(v) => setConfig({...config, modelo_folha: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pimaco_6080">Pimaco 6080 (3x10)</SelectItem>
                      <SelectItem value="pimaco_6081">Pimaco 6081 (3x8)</SelectItem>
                      <SelectItem value="pimaco_6082">Pimaco 6082 (2x5)</SelectItem>
                      <SelectItem value="pimaco_6083">Pimaco 6083 (2x10)</SelectItem>
                      <SelectItem value="pimaco_6184">Pimaco 6184 (2x7)</SelectItem>
                      <SelectItem value="pimaco_6287">Pimaco 6287 (3x11)</SelectItem>
                      <SelectItem value="colacril_ca4354">Colacril CA4354 (4x13)</SelectItem>
                      <SelectItem value="gondola_pequena">Gôndola Pequena (4x10)</SelectItem>
                      <SelectItem value="gondola_media">Gôndola Média (3x7)</SelectItem>
                      <SelectItem value="gondola_grande">Gôndola Grande (2x5)</SelectItem>
                      <SelectItem value="termica_40x25">Térmica 40x25mm</SelectItem>
                      <SelectItem value="termica_50x30">Térmica 50x30mm</SelectItem>
                      <SelectItem value="termica_60x40">Térmica 60x40mm</SelectItem>
                      <SelectItem value="personalizado">Personalizado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Tipo de Impressora</Label>
                  <Select 
                    value={config.tipo_impressora} 
                    onValueChange={(v) => setConfig({...config, tipo_impressora: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="laser">Laser</SelectItem>
                      <SelectItem value="jato_tinta">Jato de Tinta</SelectItem>
                      <SelectItem value="termica">Térmica</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {config.modelo_folha === 'personalizado' && (
                  <>
                    <Separator />
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Largura (mm)</Label>
                        <Input 
                          type="number" 
                          value={config.largura_etiqueta}
                          onChange={(e) => setConfig({...config, largura_etiqueta: parseFloat(e.target.value) || 0})}
                        />
                      </div>
                      <div>
                        <Label>Altura (mm)</Label>
                        <Input 
                          type="number" 
                          value={config.altura_etiqueta}
                          onChange={(e) => setConfig({...config, altura_etiqueta: parseFloat(e.target.value) || 0})}
                        />
                      </div>
                      <div>
                        <Label>Colunas</Label>
                        <Input 
                          type="number" 
                          value={config.colunas}
                          onChange={(e) => setConfig({...config, colunas: parseInt(e.target.value) || 1})}
                        />
                      </div>
                      <div>
                        <Label>Linhas</Label>
                        <Input 
                          type="number" 
                          value={config.linhas}
                          onChange={(e) => setConfig({...config, linhas: parseInt(e.target.value) || 1})}
                        />
                      </div>
                      <div>
                        <Label>Margem Superior (mm)</Label>
                        <Input 
                          type="number" 
                          value={config.margem_superior}
                          onChange={(e) => setConfig({...config, margem_superior: parseFloat(e.target.value) || 0})}
                        />
                      </div>
                      <div>
                        <Label>Margem Esquerda (mm)</Label>
                        <Input 
                          type="number" 
                          value={config.margem_esquerda}
                          onChange={(e) => setConfig({...config, margem_esquerda: parseFloat(e.target.value) || 0})}
                        />
                      </div>
                      <div>
                        <Label>Espaçamento H (mm)</Label>
                        <Input 
                          type="number" 
                          value={config.espacamento_horizontal}
                          onChange={(e) => setConfig({...config, espacamento_horizontal: parseFloat(e.target.value) || 0})}
                        />
                      </div>
                      <div>
                        <Label>Espaçamento V (mm)</Label>
                        <Input 
                          type="number" 
                          value={config.espacamento_vertical}
                          onChange={(e) => setConfig({...config, espacamento_vertical: parseFloat(e.target.value) || 0})}
                        />
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Conteúdo da Etiqueta */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Conteúdo da Etiqueta
                </CardTitle>
                <CardDescription>Configure o que será exibido em cada etiqueta</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Mostrar Nome do Produto</Label>
                    <Switch 
                      checked={config.mostrar_nome}
                      onCheckedChange={(v) => setConfig({...config, mostrar_nome: v})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Mostrar Código de Barras</Label>
                    <Switch 
                      checked={config.mostrar_codigo_barras}
                      onCheckedChange={(v) => setConfig({...config, mostrar_codigo_barras: v})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Mostrar Preço</Label>
                    <Switch 
                      checked={config.mostrar_preco}
                      onCheckedChange={(v) => setConfig({...config, mostrar_preco: v})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Mostrar Código Interno</Label>
                    <Switch 
                      checked={config.mostrar_codigo_interno}
                      onCheckedChange={(v) => setConfig({...config, mostrar_codigo_interno: v})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Mostrar Descrição</Label>
                    <Switch 
                      checked={config.mostrar_descricao}
                      onCheckedChange={(v) => setConfig({...config, mostrar_descricao: v})}
                    />
                  </div>
                </div>

                <Separator />

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Tamanho Fonte Nome (pt)</Label>
                    <Input 
                      type="number" 
                      value={config.tamanho_fonte_nome}
                      onChange={(e) => setConfig({...config, tamanho_fonte_nome: parseInt(e.target.value) || 10})}
                    />
                  </div>
                  <div>
                    <Label>Tamanho Fonte Preço (pt)</Label>
                    <Input 
                      type="number" 
                      value={config.tamanho_fonte_preco}
                      onChange={(e) => setConfig({...config, tamanho_fonte_preco: parseInt(e.target.value) || 14})}
                    />
                  </div>
                  <div>
                    <Label>Altura Código Barras (px)</Label>
                    <Input 
                      type="number" 
                      value={config.tamanho_codigo_barras}
                      onChange={(e) => setConfig({...config, tamanho_codigo_barras: parseInt(e.target.value) || 40})}
                    />
                  </div>
                  <div>
                    <Label>Formato Código</Label>
                    <Select 
                      value={config.formato_codigo_barras} 
                      onValueChange={(v) => setConfig({...config, formato_codigo_barras: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CODE128">CODE128 (genérico)</SelectItem>
                        <SelectItem value="EAN13">EAN-13</SelectItem>
                        <SelectItem value="EAN8">EAN-8</SelectItem>
                        <SelectItem value="UPC">UPC</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Separator />

                <Button onClick={salvarConfig} disabled={salvando} className="w-full gap-2">
                  {salvando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Salvar Configurações
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Preview da Etiqueta */}
          <Card>
            <CardHeader>
              <CardTitle>Preview da Etiqueta</CardTitle>
              <CardDescription>Visualização aproximada de como ficará a etiqueta</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex justify-center">
                <div 
                  className="border-2 border-dashed border-gray-300 p-2 flex flex-col items-center justify-center text-center"
                  style={{
                    width: `${config.largura_etiqueta * 3}px`,
                    height: `${config.altura_etiqueta * 3}px`,
                    minWidth: '120px',
                    minHeight: '80px'
                  }}
                >
                  {config.mostrar_nome && (
                    <div className="font-bold text-sm">Nome do Produto</div>
                  )}
                  {config.mostrar_codigo_barras && (
                    <div className="my-1">
                      <Barcode className="h-8 w-16" />
                    </div>
                  )}
                  {config.mostrar_preco && (
                    <div className="font-bold text-lg">R$ 99,90</div>
                  )}
                  {config.mostrar_codigo_interno && (
                    <div className="text-xs text-gray-500">Cód: 123456</div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal Preview */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview das Etiquetas</DialogTitle>
            <DialogDescription>
              {totalEtiquetas} etiqueta(s) em {totalPaginas} página(s)
            </DialogDescription>
          </DialogHeader>
          
          <div 
            className="border p-4 bg-white overflow-auto"
            style={{ maxHeight: '60vh' }}
          >
            <div 
              className="grid gap-1"
              style={{
                gridTemplateColumns: `repeat(${config.colunas}, ${config.largura_etiqueta * 2.5}px)`,
              }}
            >
              {produtosSelecionados.flatMap(produto => 
                Array(produto.quantidade).fill(null).map((_, idx) => (
                  <div 
                    key={`${produto.id}-${idx}`}
                    className="border border-dashed p-1 flex flex-col items-center justify-center text-center"
                    style={{
                      width: `${config.largura_etiqueta * 2.5}px`,
                      height: `${config.altura_etiqueta * 2.5}px`,
                    }}
                  >
                    {config.mostrar_nome && (
                      <div className="font-bold text-xs truncate w-full">{produto.name}</div>
                    )}
                    {config.mostrar_codigo_barras && (
                      <Barcode className="h-6 w-12 my-1" />
                    )}
                    {config.mostrar_preco && (
                      <div className="font-bold text-sm">{formatarPreco(produto.price)}</div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>Fechar</Button>
            <Button onClick={() => { setShowPreview(false); imprimir(); }} className="gap-2">
              <Printer className="h-4 w-4" />
              Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EtiquetasPage;
